/**
 * The dashboard contract, shared by the server that fills it and the UI that draws it.
 *
 * It lives here rather than in either side because the owner's framing makes the
 * ordering part of the contract, not a rendering choice: the dashboard SHOWS, in a
 * fixed priority, and Hermes acts. A UI free to reorder these would quietly turn a
 * tracking tower back into the flat wall of panels he said was hard to follow.
 *
 * The five questions, in the order he gave them:
 *   1. what do I need to do        -> owed
 *   2. what am I waiting on        -> waiting
 *   3. key updates                 -> updates
 *   4. what I have                 -> holdings
 *   5. ask and discover            -> a search surface, not part of this payload
 *
 * `intent` sits above all five, because without it none of the rest can be ranked.
 */

/** A commitment as the dashboard shows it. Mirrors the server's CommitmentView. */
export interface DashboardCommitment {
  text: string;
  path: string;
  counterparty: string;
  due: string;
  blockedOn: string;
  topic: string;
  /** Days until due. Negative is overdue. Null when no due date was extracted. */
  dueInDays: number | null;
  /** The objective this serves, if any. Empty when it serves nothing declared. */
  objective: string;
  /** True when the text names a flagged account. The live wall shows the item unnamed. */
  confidential?: boolean;
}

/**
 * An objective: the owner's declared intent.
 *
 * The one thing that cannot be distilled from correspondence, so it is authored.
 * Everything else on the dashboard is measured against these.
 */
export interface DashboardObjective {
  name: string;
  path: string;
  summary: string;
  horizon: string;
  status: string;
  /** Engagements declared as serving this objective. */
  engagements: string[];
  /** Commitments currently in flight against it. */
  openCommitments: number;
  /**
   * Days since anything linked to this objective changed, or null when nothing is
   * linked at all. A high number, or null, IS the gap.
   */
  staleDays: number | null;
}

/** Something the system believes needs the owner's judgement about his intent. */
export interface DashboardProposal {
  id: number;
  kind: string;
  title: string;
  ref: string;
  createdAt: string;
}

/** One meeting from the calendar mount. */
export interface DashboardMeeting {
  title: string;
  path: string;
  /** ISO start. Empty when the node has no readable time. */
  startsAt: string;
  /** Empty when unknown. */
  endsAt: string;
  /** Who called it, without the wikilink brackets. */
  organiser: string;
  /** True for a repeating series, which reads differently from a one-off. */
  recurring: boolean;
  allDay: boolean;
  /** True when it is today in UTC, so the UI can separate today from later. */
  isToday: boolean;
  /**
   * True when the meeting note is `provenance: client-confidential`. The live wall is a room
   * view that visitors see, so it names no client and no guest for such a meeting; the work
   * views keep the title. Optional so an older server simply omits it and nothing is redacted
   * that the owner has not marked.
   */
  confidential?: boolean;
}

/** What changed since the owner last looked. */
export interface DashboardUpdate {
  title: string;
  path: string;
  kind:
    | 'person'
    | 'org'
    | 'topic'
    | 'commitment'
    | 'decision'
    | 'thread'
    | 'objective'
    // A scheduled meeting, from the calendar. Distinct from 'thread', which is what
    // was actually said: one is intent, the other is record. See mo/calendar.ts.
    | 'meeting'
    // Something he published. The only node here that is his own output rather than
    // something distilled from what reached him. See mo/publishing.ts.
    | 'post';
  at: string;
  /** True when the title names a confidential account. The live wall shows the change unnamed. */
  confidential?: boolean;
}

/** The shape of the brain. Reassurance, and deliberately below the fold. */
export interface DashboardHoldings {
  counts: Record<string, number>;
  totalNotes: number;
  /** Organisations with the most current activity, most active first. */
  activeOrgs: { name: string; path: string; engagements: number; people: number }[];
}

/**
 * Gaps are the whole reason the intent layer exists: each one is a place where
 * declared intent and observed reality disagree.
 */
export interface DashboardGap {
  kind: 'objective-stalled' | 'objective-unserved' | 'engagement-unclaimed' | 'commitment-unaligned';
  title: string;
  detail: string;
  ref: string;
}

/**
 * One row of Key Actions: the smallest set that is genuinely actionable.
 *
 * Separate from `owed` and `waiting`, which are the full lists. This is the answer to
 * "what do I do now", and it is deliberately capped: the panel it feeds exists to be
 * read in one glance. `reason` is carried so the surface can explain WHY an item is
 * here, because a ranking nobody can reconstruct is a ranking nobody trusts.
 */
export interface DashboardKeyAction {
  text: string;
  path: string;
  /** `due` has a real deadline inside the horizon; `live` was agreed recently. */
  lane: 'due' | 'live';
  /** Human phrasing: "due today", "due in 3 days", "agreed 2 days ago". */
  reason: string;
  /** True when the owner owes it, false when he is waiting on someone. */
  mine: boolean;
  /** True when the text names a flagged account. The live wall shows the item unnamed. */
  confidential?: boolean;
}

/**
 * How the whole commitment set divides. Rendered so the panel can never imply that
 * six is all there is.
 *
 * Measured 2026-08-03, before triage existed: 1,068 commitments, all `status: open`,
 * 1,261 attention items with ZERO ever read. These counts are the honest version of
 * that pile.
 */
export interface DashboardLanes {
  due: number;
  live: number;
  scheduled: number;
  overdue: number;
  archive: number;
}

/**
 * Facts the system knows about its own coverage.
 *
 * This replaces the Tracking panel from his mock (Focus Time, Tasks Done, Habit
 * Score). Nothing in MO OS measures any of those, and per the A6 spec one fabricated
 * figure on a tracking tower discredits every real one. Everything here is counted,
 * never estimated, and a null means "not known" rather than zero.
 */
export interface DashboardTelemetry {
  /** Meeting nodes on disk, from the calendar poll. */
  meetings: number;
  /** Read AI webhooks accepted. */
  meetingsTranscribed: number;
  /** Last successful calendar poll, ISO, or null when it has never run. */
  calendarLastSeen: string | null;
  /** Last successful mail drain, ISO, or null. */
  mailLastRun: string | null;
  /** Open attention items by kind, so the 196 identity conflicts are visible. */
  attentionByKind: Record<string, number>;
  /** Last Graph mail sweep of his own mailbox, ISO, or null when it has never run. */
  graphMailLastRun?: string | null;
  /**
   * Is the Hermes cron scheduler alive?
   *
   * On the telemetry panel because it is the same class of failure as the Read AI token below:
   * something that dies quietly and whose normal output is silence. Cron ran NOTHING for two days
   * in August 2026 behind a config file that looked valid, and the only evidence was a traceback
   * in a container log nobody reads.
   *
   * `never` is distinct from `late`: until the heartbeat job is installed there has been no beat
   * to miss, and a permanent red row for an unwired feature teaches him to ignore the panel.
   * Optional so an older server simply omits it.
   */
  hermesCron?: {
    state: 'never' | 'ok' | 'late';
    minutesAgo: number | null;
    everyMinutes: number | null;
    job: string | null;
  };
  /**
   * The crew, with each agent's state derived rather than asserted.
   *
   * The roster used to be a hardcoded const duplicated in the two dashboard views, in which
   * `live: true` was a claim. It showed Hermes as active on every one of the nine days its cron
   * scheduler was dead, while `hermesCron` directly above said `late` the whole time. The panel
   * that answers "who is working" now reads the evidence. See `mo/crew.ts` for the rules.
   *
   * Optional so an older server simply omits it and the views fall back.
   */
  crew?: {
    name: string;
    role: string;
    plate: string;
    state: 'active' | 'late' | 'planned' | 'not built';
    detail: string;
    /** Kanji for the name, where it has one. Fawzy does not; see mo/crew.ts. */
    mark?: string;
  }[];
  /**
   * Last successful Read AI token refresh, ISO, or null.
   *
   * On the telemetry panel because Read AI is the ONE integration that can die silently: its
   * refresh token rotates on every use and a lost one cannot be repaired from the server, it
   * needs him back in a browser. A stale timestamp here is the only early warning.
   */
  readaiTokenRefreshedAt?: string | null;
  /** Last write to Twenty, ISO, or null. */
  crmLastWrite?: string | null;
  /**
   * Stage proposals waiting on him.
   *
   * The number that matters on this row is work WAITING, not work done, so it is counted
   * separately from anything the sync accomplished.
   */
  crmProposalsPending?: number;
}

/**
 * One line of the brain-health readout: what the vault is not sure about.
 *
 * The `query` is deliberately typed loosely here rather than importing the server's `AggregateQuery`:
 * the shared package must not depend on the server, and the client's only use for it is to POST it
 * back to `/api/mo/query` verbatim. It is opaque data in transit, not something the UI composes.
 */
export interface DashboardHealthRow {
  key: string;
  label: string;
  count: number;
  scanned: number;
  note: string;
  tier: 'caution' | 'advisory';
  /** Absent when the finding is not expressible as one query, so the row has no drill-down. */
  query?: unknown;
}

/**
 * Who owes the next move on an account.
 *
 * This is the field that stops the Pipeline panel misleading him, and it is why the panel
 * carries direction at all rather than just a date. "Last contact 14 days ago" is identical
 * for an account ignoring him and one he has not replied to, and the two call for opposite
 * actions. Read from message direction, never inferred from silence.
 *
 * `meetings-only` is its own case because Barter has never sent an email: any days-quiet
 * figure computed from mail would be permanently large and permanently meaningless.
 */
export type PipelineDirection = 'they-owe' | 'you-owe' | 'meetings-only' | 'unknown';

/** One live account, as the Pipeline panel shows it. */
export interface DashboardPipelineAccount {
  account: string;
  /** What Twenty SAYS, never what MO OS proposed. See `proposedStage`. */
  stage: string;
  /**
   * The stage MO OS would advance this to, or null. Deliberately separate from `stage`:
   * rendering a proposal as though it were the state would be the same fabrication the A6
   * spec forbids everywhere else. It appears as a question, not as a fact.
   */
  proposedStage: string | null;
  /** Why, in MO OS's own words, so the UI never has to compose a justification. */
  proposalWhy: string;
  followUpDueAt: string | null;
  /** Null when the account has no mail at all, which is not the same as zero days. */
  daysQuiet: number | null;
  direction: PipelineDirection;
  /** Empty when nobody at the account has ever written, rather than a guessed name. */
  pointOfContact: string;
  /** Deep link into Twenty, so a row is one click from the record it describes. */
  twentyUrl: string;
  /**
   * What the numbers on this row MEAN, in one sentence that names its own evidence.
   *
   * Deterministic, from `mo/accountVerdict.ts`, never a generation: the same account must always
   * read the same way, so that when he disagrees he is arguing with a rule and not with a model
   * that may say something different tomorrow. Optional so an older server simply omits it.
   */
  verdict?: { line: string; tone: 'act' | 'watch' | 'fine' };
  /** A second line when the relationship's BASE is the risk rather than its state. */
  contactRisk?: string;
  /**
   * True when this row's CONCLUSIONS were withheld because the lane that draws them is broken.
   * See `withholdConclusions` in mo/queries.ts.
   *
   * On the row rather than passed as a prop because two views render this panel, and because a
   * withheld reading and an unknown one are otherwise indistinguishable on screen: without this
   * the UI would render the gap as though nothing were known, which is its own quiet lie.
   */
  conclusionsWithheld?: true;
  /** How many people MO OS knows at this account, and how many it can actually name. */
  contacts?: { total: number; named: number };
  /**
   * Message counts either way, carried on the row because they exist ONLY at the ingest call
   * site: `total` and `inbound` come from the AccountState rollup, not from Twenty. Without
   * them on the row the chase lane cannot tell "nobody ever replied" from "no mail seen".
   *
   * Optional so a row published by an older build simply yields no chase rather than a wrong one.
   *
   * `lastMessageAt` and `lastWasOutbound` ride along for the same reason and are what the chase
   * lane's drop rule actually compares. The counts are per-sweep-window and can go DOWN, because
   * accountStateStore merges by recency and never sums them, so only recency can express "a newer
   * reply has arrived since he decided".
   */
  mail?: {
    total: number;
    inbound: number;
    /** The account's latest message time, or null when it has none. */
    lastMessageAt: string | null;
    /** True when that latest message was his. */
    lastWasOutbound: boolean;
  };
}

/**
 * The follow-up MO OS drafted for one chase, or its refusal to draft one.
 *
 * A REFUSAL IS A REAL ANSWER AND IS CARRIED AS ONE. He already has tools that will write him a
 * generic follow-up on demand; the thing he cannot get elsewhere is one that names something
 * specific and true, so when there is no such thing to name this says so rather than padding a
 * paragraph. `body: null` with a `why` is the honest shape, and the UI renders the reason.
 */
export interface DashboardChaseDraft {
  /** The email he can copy, or null when there is none. */
  subject: string | null;
  body: string | null;
  /** Why there is no draft. Present exactly when `body` is null. */
  why: string | null;
  /** `record` = drafted from what MO OS has read. `web` = a cold approach, drafted from a lookup. */
  grounding: 'record' | 'web' | null;
  /** The web findings behind a cold draft, so the hook can be checked rather than trusted. */
  sources: { title: string; url: string }[];
  /** Vault paths the grounding actually retrieved. Never invented. */
  citations: string[];
  /**
   * A stage change to consider. PROPOSED, NEVER APPLIED: he moves stages, and that rule predates
   * the draft lane entirely. Rendered as a question, like `proposedStage` on an account row.
   */
  proposal: { stage: string; why: string } | null;
  caveats: string[];
  /** When it was composed, so an old hook reads as old. */
  at: string;
}

/**
 * One account owed a re-approach. DERIVED on every read, never stored: see mo/chase.ts.
 */
export interface DashboardChase {
  account: string;
  /** The same sentence the accounts panel prints, from accountVerdict. */
  line: string;
  /** 1 = the next move is his. 2 = nobody has ever replied. 3 = they owe, and have for two months. */
  tier: 1 | 2 | 3;
  daysQuiet: number;
  /** A real name, or null when none is known. Never an email address. */
  recipient: string | null;
  /** Why there is no recipient, so the UI can say it rather than blanking. */
  recipientNote: string | null;
  /**
   * The drafted follow-up, when one has been produced for this account.
   *
   * OPTIONAL, and absent means the drafting pass has not reached this row yet, which is a
   * different fact from "there is nothing to say". The UI must not render the two the same way,
   * on the same rule that makes `pipeline` optional on the payload.
   */
  draft?: DashboardChaseDraft;
}

/** One open commitment that is explicitly waiting on something. */
export interface DashboardStuckItem {
  text: string;
  path: string;
  /** Verbatim from the note. Never paraphrased into a category. */
  blockedOn: string;
  counterparty: string;
  topic: string;
  /** Days since the note last moved, or null when it carries no date. */
  ageDays: number | null;
}

/**
 * What went out under his name, for the wall's chart and the brand objective's gauge.
 *
 * Fourteen days, zero-filled, so a quiet day is a visible gap rather than a missing bar. Counted
 * from `mo/posts` nodes, which the publishing lane writes from Postiz; nothing here is estimated.
 * Optional on the payload so an older server simply omits it and the views say so.
 */
export interface DashboardPublishing {
  days: { date: string; count: number }[];
  total: number;
  thisWeek: number;
  latest: { title: string; channel: string; publishedAt: string } | null;
}

export interface Dashboard {
  /** ISO timestamp this payload was built. */
  generatedAt: string;
  /** Empty when he has declared none. The UI must say so rather than render blank. */
  intent: DashboardObjective[];
  gaps: DashboardGap[];
  /**
   * What he owes, CAPPED. `owedTotal` is the real number.
   *
   * Capped because these two lists were 87% of a 290KB payload (519 and 411 items, measured
   * 2026-08-05) while the panel renders four rows. The full list is a separate fetch, so
   * opening the dashboard no longer pays for a list nobody has asked to see.
   */
  /**
   * What is actually on his calendar next, soonest first.
   *
   * The Today panel previously showed recently-CHANGED notes, which answers "what moved" and
   * not "what is my day". Both are useful and they are different questions, so meetings lead
   * and the changes sit under them.
   */
  agenda: DashboardMeeting[];
  owed: DashboardCommitment[];
  owedTotal: number;
  waiting: DashboardCommitment[];
  waitingTotal: number;
  /** The six-ish things worth acting on, and how many were not shown. */
  keyActions: DashboardKeyAction[];
  keyActionsMore: number;
  lanes: DashboardLanes;
  telemetry: DashboardTelemetry;
  updates: DashboardUpdate[];
  holdings: DashboardHoldings;
  publishing?: DashboardPublishing;
  /** Objective and identity questions awaiting his accept or reject. CAPPED. */
  proposals: DashboardProposal[];
  /**
   * How many open questions were NOT shown, after filtering and capping.
   *
   * Present so the cap is stated rather than silent, the same rule `keyActionsMore` follows.
   * A panel showing three of twenty with no hint of the other seventeen reads as "you have
   * three questions", which is the quiet kind of wrong this surface exists to avoid.
   */
  proposalsMore: number;
  /**
   * The daily brief Hermes pushes: inbox state, calories, headlines.
   *
   * Optional so an older server, or one Hermes has never pushed to, simply omits it and the
   * panel does not render. Each slot is independently nullable because the three arrive on
   * different rhythms, and every one carries its own `at` so the UI can say how old it is
   * rather than presenting four-hour-old numbers as current.
   */
  daily?: DashboardDaily;
  /**
   * What the brain is not sure about. Optional so an older server simply omits the panel.
   *
   * On the payload rather than a separate fetch because every row is a count over notes the
   * dashboard has already loaded, so it costs nothing extra, and because a panel about honesty
   * should not be the one thing that arrives late.
   */
  health?: DashboardHealthRow[];
  /**
   * The live accounts and who owes the next move.
   *
   * Optional so a server without CRM credentials, or one whose Twenty call failed, simply
   * omits the panel. That is deliberate: a Pipeline panel showing an empty list is
   * indistinguishable from "you have no live accounts", which is a lie of exactly the kind
   * this surface exists to avoid. Absent means unknown; present means counted.
   */
  pipeline?: DashboardPipelineAccount[];
  /**
   * Accounts owed a chase, ranked. ABSENT means not known, the CRM did not answer, which the
   * UI must render differently from an empty queue, exactly as `pipeline` already requires.
   */
  chases?: DashboardChase[];
  /**
   * Open commitments explicitly waiting on something, oldest first, CAPPED.
   *
   * `blocked_on` has been populated since A2 and nothing has ever read it. `stuckTotal` is the
   * real count, on the same rule as `owedTotal`.
   */
  stuck: DashboardStuckItem[];
  stuckTotal: number;
  /*
   * `contactRisk` was a panel here and is GONE, folded onto the account row instead.
   *
   * It counted contacts from the VAULT while the never-written-to panel counted them from TWENTY,
   * so on 2026-08-11 one panel said AMEK, Barter and Red Sea Global had nobody on record while the
   * CRM held a contact for all fifteen accounts. Two answers to one question on one screen is what
   * he called "sporadic". There is now one contact model (mo/accountContacts.ts) and its answer
   * rides on the row it describes.
   */
  /**
   * What this payload cannot know, rendered verbatim. A surface implying it knows
   * his calendar when it does not is worse than one that admits it.
   */
  limitations: string[];
}

export interface DashboardDailyInbox {
  unread: number;
  cleared?: number;
  at: string;
}

export interface DashboardDailyCalories {
  consumed: number;
  target?: number;
  at: string;
}

export interface DashboardDailyNewsItem {
  title: string;
  url?: string;
  source?: string;
}

export interface DashboardDaily {
  inbox: DashboardDailyInbox | null;
  calories: DashboardDailyCalories | null;
  news: { items: DashboardDailyNewsItem[]; at: string } | null;
}

/**
 * One name an objective can be declared against.
 *
 * Shared because it crosses the wire: the server derives it from the vault (`mo/engagements.ts`)
 * and the declare form is the only consumer. See that module for why a free-text engagement box
 * was not good enough.
 */
export interface EngagementCandidate {
  name: string;
  type: 'topic' | 'org';
  /** ISO day of the most recent activity we can evidence. Never empty. */
  lastSeen: string;
  /** The objective already claiming this name, or '' when it is free. */
  claimedBy: string;
  /** `active`, `dormant`, or '' when the note carries no status. */
  status: string;
}

/**
 * The name key everything in MO OS matches on.
 *
 * CANONICAL, and it lives in shared for one reason: the objective declare form has to tell
 * him whether a name resolves, and it can only do that honestly if it folds names exactly as
 * the server's attribution does. A second implementation in the browser would drift, and the
 * failure would be silent and in the worst place: the form would accept a name the deck then
 * never counts.
 *
 * Diacritics are folded because the corpus is materially Spanish, Catalan and Arabic, so
 * `Fundacio` and `Fundació` are one engagement. Punctuation becomes space rather than being
 * deleted, so `Cognvai, Vantage` and `Cognvai Vantage` agree.
 */
export function normalizeName(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[.,;:'"()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Which of `names` nothing can be attributed through.
 *
 * Shared for the same reason `normalizeName` is: the declare form has to agree with the
 * server's attribution exactly. Returns [] when `candidates` is empty, because "the list has
 * not loaded" and "every name is wrong" are different states and only one of them is worth
 * warning about.
 */
export function unresolvedEngagements(
  names: readonly string[],
  candidates: readonly EngagementCandidate[],
): string[] {
  if (candidates.length === 0) return [];
  const known = new Set(candidates.map((c) => normalizeName(c.name)));
  const out: string[] = [];
  for (const n of names) {
    const key = normalizeName(n);
    if (key === '' || known.has(key)) continue;
    if (!out.includes(n)) out.push(n);
  }
  return out;
}
